import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

const client = generateClient();

// Domain key mapping to convert between numerical indices and text descriptions
export const getACCSDomainKeyByIndex = (index) => {
  const domainKeys = [
    "MSRA: Clinical Problem Solving",
    "MSRA: Professional Dilemmas",
    "Interview: Commitment to Specialty",
    "Interview: Teamwork & Stress",
    "Interview: Teaching & QI",
    "Interview: EM Knowledge",
    "Interview: Clinical Scenarios"
  ];
  
  // Convert to number and ensure it's handled as zero-based index
  const numericIndex = Number(index);
  console.log(`Converting ACCS domain index ${index} (type: ${typeof index}) to key, numeric value: ${numericIndex}`);
  
  if (isNaN(numericIndex)) {
    console.error(`Invalid ACCS domain index: ${index} is not a number`);
    return `Unknown domain ${index}`;
  }
  
  // Use zero-based indexing
  const result = domainKeys[numericIndex];
  console.log(`ACCS domain lookup result for index ${numericIndex}: ${result}`);
  
  // Return the domain name or "Unknown domain" if index is out of range
  return result || `Unknown domain ${index}`;
};

// Helper function to determine if a domain key is numeric
const isNumericDomainKey = (key) => {
  const result = !isNaN(Number(key)) && key !== null && key !== '';
  console.log(`Checking if ACCS domain key "${key}" is numeric: ${result}`);
  return result;
};

/**
 * Saves or updates progress for an ACCS domain
 * @param {string|number} domainKey - The key identifying the domain/category (can be number or string)
 * @param {number} progress - The progress value to save (number of completed tasks)
 * @returns {Promise} - The GraphQL mutation result
 */
export const saveACCSDomainProgress = async (domainKey, progress) => {
  try {
    console.log(`ACCS domain key before conversion: "${domainKey}" (type: ${typeof domainKey})`);
    
    // Convert numeric domainKey to text description
    const textDomainKey = isNumericDomainKey(domainKey) 
      ? getACCSDomainKeyByIndex(Number(domainKey))
      : domainKey;
    
    console.log(`ACCS domain key after conversion: "${textDomainKey}"`);
    
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated');
      throw new Error('User not authenticated');
    }
    console.log('User authenticated:', user.username);

    // Check if there's already a record for this domain
    console.log(`Checking for existing record with userId: ${user.username}, domainKey: ${textDomainKey}`);
    const { data } = await client.graphql({
      query: queries.listACCSDomainProgresses,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          domainKey: { eq: textDomainKey }
        }
      },
      authMode: 'userPool'
    });
    console.log('Query result:', JSON.stringify(data));

    const existingRecord = data.listACCSDomainProgresses.items[0];

    if (existingRecord) {
      // Update existing record
      console.log(`Updating existing record ${existingRecord.id} with progress: ${progress}`);
      const input = {
        id: existingRecord.id,
        userId: user.username,
        domainKey: textDomainKey,
        progress,
        owner: user.username,
        _version: existingRecord._version // Include version if using DataStore
      };

      console.log('Update input:', JSON.stringify(input));
      const result = await client.graphql({
        query: mutations.updateACCSDomainProgress,
        variables: { input },
        authMode: 'userPool'
      });

      console.log(`Updated ACCS domain progress for ${textDomainKey}:`, result);
      return result;
    } else {
      // Create new record
      console.log(`Creating new record for userId: ${user.username}, domainKey: ${textDomainKey}, progress: ${progress}`);
      const input = {
        userId: user.username,
        domainKey: textDomainKey,
        progress,
        owner: user.username
      };

      console.log('Create input:', JSON.stringify(input));
      const result = await client.graphql({
        query: mutations.createACCSDomainProgress,
        variables: { input },
        authMode: 'userPool'
      });

      console.log(`Created ACCS domain progress for ${textDomainKey}:`, result);
      return result;
    }
  } catch (error) {
    console.error('Error saving ACCS domain progress:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      errors: error.errors || 'No nested errors'
    });
    throw error;
  }
};

/**
 * Fetches progress for all ACCS domains for the current user
 * @returns {Promise<Array>} - Array of domain progress objects
 */
export const fetchACCSDomainsProgress = async () => {
  try {
    console.log('Fetching progress for all ACCS domains');
    
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated during fetch all');
      throw new Error('User not authenticated');
    }
    console.log(`Fetching all domains for user: ${user.username}`);

    const { data } = await client.graphql({
      query: queries.listACCSDomainProgresses,
      variables: {
        filter: { userId: { eq: user.username } },
        limit: 100 // Ensure we get all records
      },
      authMode: 'userPool'
    });

    console.log('Fetched all ACCS domain progress items:', data.listACCSDomainProgresses.items.length);
    console.log('Items:', JSON.stringify(data.listACCSDomainProgresses.items));
    return data.listACCSDomainProgresses.items;
  } catch (error) {
    console.error('Error fetching ACCS domains progress:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    return [];
  }
};

/**
 * Fetches progress for a specific ACCS domain
 * @param {string|number} domainKey - The key identifying the domain/category
 * @returns {Promise<Object>} - The domain progress object or null if not found
 */
export const fetchACCSDomainProgress = async (domainKey) => {
  try {
    console.log(`Fetching ACCS domain progress for domain key: "${domainKey}" (type: ${typeof domainKey})`);
    
    // Convert numeric domainKey to text description using the same logic as saveACCSDomainProgress
    const textDomainKey = isNumericDomainKey(domainKey) 
      ? getACCSDomainKeyByIndex(Number(domainKey))
      : domainKey;
    
    console.log(`ACCS domain key after conversion for fetch: "${textDomainKey}"`);
      
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      console.error('Authentication error: User not authenticated during fetch');
      throw new Error('User not authenticated');
    }
    console.log(`Fetching for user: ${user.username}, ACCS domain: ${textDomainKey}`);

    const { data } = await client.graphql({
      query: queries.listACCSDomainProgresses,
      variables: {
        filter: { 
          userId: { eq: user.username }, 
          domainKey: { eq: textDomainKey }
        }
      },
      authMode: 'userPool'
    });

    console.log(`Fetch result for ACCS domain ${textDomainKey}:`, JSON.stringify(data.listACCSDomainProgresses.items));

    if (data.listACCSDomainProgresses.items.length > 0) {
      console.log(`Found existing progress for ACCS domain ${textDomainKey}:`, data.listACCSDomainProgresses.items[0].progress);
      return data.listACCSDomainProgresses.items[0];
    }
    
    console.log(`No progress found for ACCS domain ${textDomainKey}`);
    return null;
  } catch (error) {
    console.error(`Error fetching ACCS domain progress for ${domainKey}:`, error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      errors: error.errors || 'No nested errors'
    });
    return null;
  }
};
